import { useOutletContext, useNavigate } from 'react-router'
import clsx from 'clsx'
import { FaArrowLeft } from 'react-icons/fa'

import { useGetUserFollowsQuery } from '../../services/apiSlice'
import { CurrentUser } from '../../services/schema/User'
import Filler from '../../components/utils/Filler'
import Spinner from '../../components/loading/Spinner'
import UserCard from '../../components/user/UserCard'
import NotFound from '../../components/fallback/NotFound'

interface Props {
  type: string
}

const tabs = [
  { titleBig: 'Following', titleSmall: 'following', id: 0 },
  { titleBig: 'Followers', titleSmall: 'followers', id: 1 },
]

const emptyFollowingText = 'not follow anyone yet.'
const emptyFollowersText = 'not have any followers yet.'

const emptySubFollowingText = [
  'When ',
  ' follow someone, you will see them here.',
  ' follows someone, you will see them here.',
]
const emptySubFollowersText = [
  'When someone follows ',
  ', it will be shown here.',
]

const FollowList: React.FC<Props> = ({ type }) => {
  const navigate = useNavigate()
  // we should get username from url
  const parts = location.pathname.split('/')
  const userToShow = parts[1]
  const route = parts[2]
  const { username: currUser, userFollowsCount } =
    useOutletContext<CurrentUser>()

  const { data, isSuccess, isError } = useGetUserFollowsQuery({
    username: userToShow,
  })

  // this is the loading component
  let main = (
    <>
      <div className="relative flex w-full grow">
        <div className="h-dvh" />
        <div className="border-theme bgd-theme relative flex h-full w-full max-w-[600px] min-w-0 grow flex-col items-stretch border-r border-b border-l md:mr-0">
          <Filler />
          <div className="border-theme sticky top-0 left-0 z-[100] flex h-[60px] w-full items-center border-b bg-inherit pl-[10px] text-xl font-bold">
            <button
              onClick={() => {
                navigate(`/${userToShow}`, {
                  replace: true,
                })
              }}
              className="hover-main flex h-[40px] w-[40px] items-center justify-center rounded-full text-sm"
            >
              <FaArrowLeft />
            </button>
            <p className="pl-[20px] tracking-wide">Loading...</p>
          </div>
          <div className="relative flex flex-col items-center pt-[150px]">
            <Spinner />
          </div>
        </div>
      </div>
    </>
  )

  if (data && isSuccess) {
    const followsToShow =
      route === 'following' ? data.following : data.followers

    const emptyFollowsText =
      route === 'following' ? emptyFollowingText : emptyFollowersText

    const emptyFollowsDisplay =
      userToShow === currUser
        ? `You do ${emptyFollowsText}`
        : `@${userToShow} does ${emptyFollowsText}`

    const emptySubFollowsDisplay =
      route === 'following'
        ? subDisplayConstructor(
            'following',
            userToShow,
            userToShow === currUser,
          )
        : subDisplayConstructor(
            'following',
            userToShow,
            userToShow === currUser,
          )

    main = (
      <>
        <div className="relative flex w-full grow">
          <div className="h-dvh" />
          <div className="border-theme bgd-theme relative flex h-full w-full max-w-[600px] min-w-0 grow flex-col items-stretch border-r border-b border-l md:mr-0">
            <Filler />
            <div className="border-theme sticky top-0 left-0 z-[50] flex h-[60px] w-full items-center bg-inherit pl-[10px] text-xl font-bold">
              <button
                onClick={() => {
                  navigate(`/${userToShow}`, {
                    replace: true,
                  })
                }}
                className="hover-main flex h-[40px] w-[40px] items-center justify-center rounded-full text-sm"
              >
                <FaArrowLeft />
              </button>
              <p className="pl-[20px] tracking-wide">{userToShow}</p>
            </div>
            <div className="relative flex w-full flex-col items-center">
              {/* ROWS - TODO:REFACTOR-same with home header */}
              <div className="border-theme flex h-[50px] w-full border-b bg-transparent">
                {tabs.map((tab, i) => {
                  return (
                    <div
                      key={tab.titleBig + i}
                      className="hover-main relative flex h-full flex-1 items-center justify-center px-[20px]"
                      onClick={() => {
                        navigate(`/${userToShow}/${tab.titleSmall}`, {
                          replace: true,
                        })
                      }}
                    >
                      <div className="relative flex h-full items-center justify-center px-[10px] font-semibold tracking-tight">
                        <p
                          className={clsx(
                            'flex w-full items-center text-nowrap',
                            {
                              'text-main': route === tab.titleSmall,
                              'text-sub': route !== tab.titleSmall,
                            },
                          )}
                        >
                          {tab.titleBig}
                        </p>
                        {route === tab.titleSmall && (
                          <div className="bgd-blue absolute bottom-0 h-[3px] w-full"></div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="flex w-full flex-col">
              {followsToShow.length === 0 && (
                <div className="font-base mt-[50px] flex w-full flex-col px-[100px] text-2xl">
                  <p className="font-semibold">{emptyFollowsDisplay}</p>
                  <p className="text-sub mt-[15px] text-base">
                    {emptySubFollowsDisplay}
                  </p>
                </div>
              )}
              {followsToShow.map((follow, idx) => {
                return (
                  <UserCard
                    key={follow.username + idx + follow.displayName}
                    user={follow}
                  />
                )
              })}
            </div>
          </div>
        </div>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <NotFound />
      </>
    )
  }

  return <>{main}</>
}

export default FollowList

const subDisplayConstructor = (
  type: string,
  user: string,
  isCurr: boolean,
): string | undefined => {
  if (type === 'following') {
    const array: string[] = []
    array.push(emptySubFollowingText[0])
    if (isCurr) {
      array.push('you')
      array.push(emptySubFollowingText[1])
    } else {
      array.push(`@${user}`)
      array.push(emptySubFollowingText[2])
    }
    return array.join('')
  }

  if (type === 'followers') {
    const array: string[] = []
    array.push(emptySubFollowersText[0])
    if (isCurr) {
      array.push('you')
      array.push(emptySubFollowersText[1])
    } else {
      array.push(`@${user}`)
      array.push(emptySubFollowersText[1])
    }
    return array.join('')
  }
}
